import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Button,
  Icon,
  Image,
  Modal,
  useMediaQuery,
  Text,
} from '@lounge-fe/ui-kit'
import { SignupModalProps } from './types'
import { SignupModalForm } from './SignupModalForm'
import classNames from 'classnames'
import { SignupSuccessContent } from './SignupSuccessContent'

export const SignupModal = ({
  data: {
    modal_title,
    modal_subtitle,
    modal_success_title,
    modal_success_promo,
    modal_success_body,
    modal_success_disclaimer_text,
    modal_success_button_text,
    modal_success_button_url,
    image,
  },
  children,
  exclusive,
  prefilledEmail,
  open,
  onOpenChange,
  ...props
}: SignupModalProps) => {
  const [hasSignedUp, setHasSignedUp] = useState(false)

  const isDesktop = useMediaQuery(
    ({ screens }) => `(min-width: ${screens['sm']})`,
  )

  const openProps =
    open !== undefined
      ? {
          isOpen: open,
          onOpen: () => open && onOpenChange?.(true),
          onClose: () => !open && onOpenChange?.(false),
        }
      : undefined

  return (
    <div {...props} data-testid="signup-modal">
      <Modal exclusive={exclusive} {...openProps}>
        {(modalBag) => (
          <>
            {typeof children === 'function' ? children(modalBag) : children}
            <Modal.Content className="sm:max-w-[738px]" size="lg">
              <Modal.Body
                className={classNames('flex flex-row justify-center p-0')}
              >
                {image && image.src && isDesktop && (
                  <div
                    className={classNames(
                      'border-b-0 w-full !p-0 !m-0 max-w-[298px]',
                    )}
                  >
                    <div className="h-full flex-shrink-0 relative">
                      <Image
                        className={classNames(
                          'w-full h-full object-cover aspect-[15/29]',
                        )}
                        data={{
                          src: image.src,
                          height: 600,
                          width: 1200,
                        }}
                      />
                    </div>
                  </div>
                )}

                <div
                  className={classNames(
                    'mx-auto w-full flex flex-col gap-3 h-full',
                    isDesktop ? 'justify-start p-8' : 'justify-center p-6',
                    hasSignedUp ? 'self-center' : 'self-start',
                    image?.src && 'sm:max-w-[440px]',
                  )}
                >
                  <Modal.Header
                    className={classNames(
                      'border-b-0 w-full !p-0 !m-0 !static',
                    )}
                  >
                    <div className="flex flex-row justify-between">
                      {((hasSignedUp && modal_success_title) ||
                        (!hasSignedUp && modal_title)) && (
                        <div
                          className="text-left"
                          dangerouslySetInnerHTML={{
                            __html: hasSignedUp
                              ? modal_success_title
                              : modal_title,
                          }}
                        />
                      )}
                      <Modal.CloseButton
                        {...openProps?.onClose}
                        className={classNames(
                          'top-0 right-0 !bg-transparent',
                          isDesktop ? 'top-8 !right-5' : 'top-6 !right-4',
                        )}
                        variant="ghost"
                      />
                    </div>
                  </Modal.Header>
                  <div>
                    {!hasSignedUp && modal_subtitle && (
                      <div
                        className="text-left mb-3"
                        dangerouslySetInnerHTML={{
                          __html: modal_subtitle,
                        }}
                      />
                    )}
                    {hasSignedUp ? (
                      <SignupSuccessContent
                        promo={modal_success_promo}
                        body={modal_success_body}
                        disclaimer={modal_success_disclaimer_text}
                        buttonText={modal_success_button_text}
                        buttonUrl={modal_success_button_url}
                      />
                    ) : (
                      <SignupModalForm
                        email={prefilledEmail}
                        onSuccess={() => {
                          setHasSignedUp(true)
                        }}
                      />
                    )}
                  </div>
                </div>
              </Modal.Body>
            </Modal.Content>
          </>
        )}
      </Modal>
    </div>
  )
}

SignupModal.Trigger = () => {
  const { t } = useTranslation()
  return (
    <Modal.Trigger>
      <Button
        type="submit"
        size="md"
        className="text-white w-full whitespace-nowrap"
        aria-label={t('action.sign_up')}
      >
        {t('action.sign_up')}
      </Button>
    </Modal.Trigger>
  )
}
